import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { AlertBadge } from "@/components/AlertBadge";
import TodayJobs from "@/components/jobs/TodayJobs";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, CalendarDays, Users, Sparkles, UsersRound, Settings as SettingsIcon,
  MessageSquare, BellRing, CalendarPlus, UserPlus, FileText, MapPinned, Target,
} from "lucide-react";

const isToday = (iso) => iso && new Date(iso).toDateString() === new Date().toDateString();
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

function Tile({ tile, index }) {
  const { icon: Icon, title, stat, to, onClick, id, alert } = tile;
  const cls = `w-[calc(50%-10px)] md:w-[calc(33.333%-14px)] lg:w-[calc(25%-15px)] group relative flex flex-col items-center justify-center text-center gap-3 px-4 py-7 rounded-2xl animate-fade-in
    bg-[hsl(330_60%_97%)] border border-[hsl(330_40%_92%)] shadow-[0_2px_8px_-2px_hsl(330_30%_40%/0.12)]
    transition-[transform,box-shadow,background-color] duration-300
    hover:-translate-y-0.5 hover:bg-[hsl(330_70%_95%)] hover:shadow-[0_10px_24px_-10px_hsl(330_60%_50%/0.3)]`;
  const body = (
    <>
      <AlertBadge count={alert?.count} className="absolute top-3 right-3" testid={`home-tile-${id}-badge`} />
      <span className="w-14 h-14 rounded-xl flex items-center justify-center bg-[hsl(330_80%_92%)] grad-ink transition-transform duration-300 group-hover:scale-105">
        <Icon className="w-7 h-7" strokeWidth={1.6} />
      </span>
      <div className="font-medium text-base text-foreground">{title}</div>
      {alert?.count > 0 && (
        <div className="text-xs font-medium text-destructive -mt-2" data-testid={`home-tile-${id}-alert`}>{alert.label}</div>
      )}
      {stat != null && !(alert?.count > 0) && (
        <div className="text-xs text-muted-foreground -mt-2" data-testid={`home-tile-${id}-stat`}>{stat}</div>
      )}
    </>
  );
  const style = { animationDelay: `${index * 40}ms` };
  return onClick ? (
    <button type="button" onClick={onClick} className={cls} style={style} data-testid={`home-tile-${id}`}>{body}</button>
  ) : (
    <Link to={to} className={cls} style={style} data-testid={`home-tile-${id}`}>{body}</Link>
  );
}

export default function Home() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [sms, setSms] = useState(null);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {});
    if (isAdmin) api.get("/sms/weekly-summary").then((r) => setSms(r.data)).catch(() => {});
  }, [isAdmin]);

  const today = jobs.filter((j) => isToday(j.scheduled_date) && j.status !== "cancelled").length;
  const s = (v, f) => (stats ? f(v ?? 0) : null);

  const adminTiles = [
    { id: "schedule", icon: CalendarDays, title: "Schedule", desc: "Day, week and month views of every clean.", to: "/jobs", stat: plural(today, "booking") + " today", feature: true },
    { id: "new-booking", icon: CalendarPlus, title: "New booking", desc: "Book a clean in a few taps.", to: "/jobs?new=1", stat: "Quick action" },
    { id: "add-client", icon: UserPlus, title: "Add client", desc: "Save a new client's details.", to: "/customers?new=1", stat: "Quick action" },
    { id: "dashboard", icon: LayoutDashboard, title: "Dashboard", desc: "Revenue, completed jobs and texts this week.", to: "/dashboard", stat: s(stats?.upcoming_jobs, (n) => `${n} upcoming`) },
    { id: "map", icon: MapPinned, title: "Live Map", to: "/map", stat: "Where cleaners are now" },
    { id: "clients", icon: Users, title: "Clients", desc: "Contacts, addresses and booking history.", to: "/customers", stat: s(stats?.customers, (n) => plural(n, "client")) },
    { id: "leads", icon: Target, title: "Leads", to: "/leads", stat: s(stats?.open_leads, (n) => `${n} open`) },
    { id: "packages", icon: Sparkles, title: "Packages", desc: "Cleaning services and hourly rates.", to: "/packages", stat: s(stats?.packages, (n) => plural(n, "package")) },
    { id: "team", icon: UsersRound, title: "Team", desc: "Cleaners, availability and logins.", to: "/team", stat: s(stats?.team_members, (n) => plural(n, "member")) },
    { id: "sms", icon: MessageSquare, title: "SMS Notifications", desc: "Reminders, templates and delivery history.", to: "/settings/sms", stat: sms ? (sms.failed ? `${sms.failed} failed this week` : `${sms.sent} sent this week`) : null },
    { id: "reports", icon: FileText, title: "Reports", desc: "", to: "/reports", stat: "Tax statements & timesheets" },
    { id: "settings", icon: SettingsIcon, title: "Settings", desc: "Business details, logo, hours and email.", to: "/settings", stat: "Brand & preferences" },
  ];
  const staffTiles = [
    { id: "schedule", icon: CalendarDays, title: "My Schedule", desc: "Your assigned cleans, photos and Mark complete.", to: "/jobs", stat: plural(today, "job") + " today", feature: true },
    { id: "packages", icon: Sparkles, title: "Packages", desc: "What each cleaning service includes.", to: "/packages", stat: "Services" },
    { id: "team", icon: UsersRound, title: "Team", desc: "Who you're working with.", to: "/team", stat: "Your colleagues" },
    { id: "notifications", icon: BellRing, title: "My notifications", desc: "Choose which texts you receive.", stat: "Text preferences", onClick: () => window.dispatchEvent(new Event("open-my-notifications")) },
  ];
  const { alerts = {} } = useOutletContext() || {};
  const tiles = (isAdmin ? adminTiles : staffTiles).map((t) => ({ ...t, alert: alerts[t.id] }));

  return (
    <div data-testid="home-page">
      <div className="mb-10 text-center animate-fade-in">
        <div className="label-caps text-muted-foreground">
          {new Date().toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", timeZone: "Australia/Brisbane" })}
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl leading-tight pb-1 mt-2 text-brand-gradient" data-testid="home-greeting">
          Welcome back, {user?.name?.split(" ")[0] || "there"}
        </h1>
        <p className="text-muted-foreground mt-1">Choose a section to get started</p>
      </div>
      {!isAdmin && <div className="max-w-5xl mx-auto"><TodayJobs /></div>}
      <div className="flex flex-wrap justify-center gap-5 mx-auto max-w-5xl" data-testid="home-tiles">
        {tiles.map((t, i) => <Tile key={t.id} tile={t} index={i} />)}
      </div>
    </div>
  );
}
